import { useMemo } from "react"

import { QueryFunction, QueryKey, useQuery } from "@tanstack/react-query"

import { compareValues, filterFakeJobs, getFakeJobs } from "../../common/fakeJobsUtils"
import { getErrorMessage } from "../../common/utils"
import { getConfig } from "../../config"
import { Job, JobFilter, JobKey, JobOrder } from "../../models/lookoutModels"
import { useMirroredLookoutApiFetch } from "../../oidcAuth"

export interface GetJobsParams {
  filters: JobFilter[]
  activeJobSets: boolean
  order: JobOrder
  skip: number
  take: number
}

export interface GetJobsResponse {
  jobs: Job[]
}

export function getFakeJobsResponse(params: GetJobsParams): GetJobsResponse {
  const filtered = filterFakeJobs(getFakeJobs(), params.filters, params.activeJobSets)
  const sorted = [...filtered].sort((a, b) =>
    compareValues(
      (a as Record<string, unknown>)[params.order.field as JobKey],
      (b as Record<string, unknown>)[params.order.field as JobKey],
      params.order.direction,
    ),
  )
  return { jobs: sorted.slice(params.skip, params.skip + params.take) }
}

const getQueryFn =
  (
    params: GetJobsParams,
    fetchFunc: GlobalFetch["fetch"],
    backend: string | undefined,
    fakeDataEnabled: boolean,
  ): QueryFunction<GetJobsResponse, QueryKey, never> =>
  async ({ signal }) => {
    try {
      if (fakeDataEnabled) {
        return getFakeJobsResponse(params)
      }

      let path = "/api/v1/jobs"
      if (backend) {
        path += "?" + new URLSearchParams({ backend })
      }

      const response = await fetchFunc(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filters: params.filters,
          activeJobSets: params.activeJobSets,
          order: params.order,
          skip: params.skip,
          take: params.take,
        }),
        signal,
      })

      const json = await response.json()
      return {
        jobs: json.jobs ?? [],
      }
    } catch (e) {
      throw await getErrorMessage(e)
    }
  }

export const useGetJobs = (params: GetJobsParams, enabled = true) => {
  const config = getConfig()
  const lookoutApiFetch = useMirroredLookoutApiFetch()

  const queryFn = useMemo(
    () => getQueryFn(params, lookoutApiFetch, config.backend, config.fakeDataEnabled),
    [params, lookoutApiFetch, config.backend, config.fakeDataEnabled],
  )

  return useQuery<GetJobsResponse, string>({
    queryKey: [
      "getJobs",
      params.filters,
      params.activeJobSets,
      params.order,
      params.skip,
      params.take,
      config.backend,
      config.fakeDataEnabled,
    ],
    queryFn,
    enabled,
    refetchOnMount: false,
    staleTime: 30_000,
  })
}
